import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './CartList.css';

function CartList({ batch }) {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [cartQuantityPrompt, setCartQuantityPrompt] = useState(null);
  const [cartQuantityInput, setCartQuantityInput] = useState('');

  useEffect(() => {
    if (batch?.id) {
      fetchCarts();
    }
  }, [batch?.id]);

  const fetchCarts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/batches/${batch.id}/carts`);
      setCarts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching carts:', err);
      setError('Failed to load carts');
    } finally {
      setLoading(false);
    }
  };

  const generateCarts = async () => {
    if (generating) return;

    // Check if any work orders are missing cart quantity
    const missingCartQuantity = batch.batch_work_orders.find(
      wo => !wo.cart_quantity || wo.cart_quantity === 0
    );

    if (missingCartQuantity) {
      setCartQuantityPrompt(missingCartQuantity);
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`/api/batches/${batch.id}/generate-carts`);
      setCarts(response.data);
      setError(null);
      alert(`Generated ${response.data.length} carts successfully!`);
    } catch (err) {
      console.error('Error generating carts:', err);
      setError('Failed to generate carts');
      alert('Failed to generate carts: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleCartQuantitySubmit = async () => {
    const quantity = parseInt(cartQuantityInput);
    if (!quantity || quantity <= 0) {
      alert('Please enter a valid cart quantity');
      return;
    }

    try {
      // Update the cart quantity in NetSuite
      await axios.patch(`/api/items/${cartQuantityPrompt.item_sku}/cart-quantity`, {
        cart_quantity: quantity
      });

      // Update ALL work orders with the same item_sku
      batch.batch_work_orders.forEach(wo => {
        if (wo.item_sku === cartQuantityPrompt.item_sku) {
          wo.cart_quantity = quantity;
        }
      });

      // Clear prompt and continue with generation
      setCartQuantityPrompt(null);
      setCartQuantityInput('');

      // Refetch batch to ensure we have latest data
      await fetchCarts();

      // Try to generate carts again
      generateCarts();
    } catch (err) {
      console.error('Error updating cart quantity:', err);
      alert('Failed to update cart quantity: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleStatusChange = async (cartId, newStatus) => {
    try {
      await axios.patch(`/api/carts/${cartId}/status`, { status: newStatus });
      await fetchCarts();
    } catch (err) {
      console.error('Error updating cart status:', err);
      alert('Failed to update cart status');
    }
  };

  const handleDeleteCart = async (cartId) => {
    if (!window.confirm('Are you sure you want to delete this cart?')) {
      return;
    }

    try {
      await axios.delete(`/api/carts/${cartId}`);
      await fetchCarts();
    } catch (err) {
      console.error('Error deleting cart:', err);
      alert('Failed to delete cart');
    }
  };

  const handlePrintTraveler = (cart) => {
    // Open batch PDF with all cart sheets in new tab
    window.open(`/api/batches/${batch.id}/carts/pdf`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return '#999';
      case 'in_use':
        return '#ff9800';
      case 'completed':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchCarts}>Retry</button>
      </div>
    );
  }

  return (
    <div className="cart-list">
      <div className="cart-list-header">
        <h4>Carts ({carts.length})</h4>
        <div className="cart-list-actions">
          {carts.length > 0 && (
            <button
              onClick={() => window.open(`/api/batches/${batch.id}/carts/pdf`, '_blank')}
              className="print-all-btn"
            >
              Print All Cart Sheets
            </button>
          )}
          {carts.length === 0 ? (
            <button
              onClick={generateCarts}
              className="generate-btn"
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate Carts'}
            </button>
          ) : (
            <button
              onClick={generateCarts}
              className="regenerate-btn"
              disabled={generating}
            >
              {generating ? 'Regenerating...' : 'Regenerate Carts'}
            </button>
          )}
        </div>
      </div>

      {carts.length === 0 ? (
        <div className="no-carts">
          <p>No carts generated yet for this batch.</p>
          <p>Click "Generate Carts" to automatically create optimal cart layouts.</p>
        </div>
      ) : (
        <table className="carts-table">
          <thead>
            <tr>
              <th>Cart Number</th>
              <th>Utilization</th>
              <th>Total Vessels</th>
              <th>Items</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {carts.map(cart => (
              <tr key={cart.id}>
                <td className="cart-number">{cart.cart_number}</td>
                <td>{Math.round(cart.utilization_percentage)}%</td>
                <td>{cart.total_vessels}</td>
                <td className="cart-items">
                  {cart.cart_items && (
                    <div className="items-summary">
                      {Object.entries(
                        cart.cart_items.reduce((acc, item) => {
                          const key = item.vessel_name || item.vessel_sku;
                          if (!acc[key]) {
                            acc[key] = 0;
                          }
                          acc[key] += item.quantity;
                          return acc;
                        }, {})
                      ).map(([name, quantity]) => (
                        <span key={name} className="item-chip">
                          {name}: {quantity}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="cart-actions">
                  <button
                    onClick={() => handlePrintTraveler(cart)}
                    className="action-btn print-btn"
                  >
                    Print
                  </button>
                  <button
                    onClick={() => handleDeleteCart(cart.id)}
                    className="action-btn delete-btn"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Cart Quantity Prompt Modal */}
      {cartQuantityPrompt && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Cart Quantity Required</h3>
            <p>
              The item "{cartQuantityPrompt.item_description}" ({cartQuantityPrompt.item_sku}) 
              is missing cart quantity information.
            </p>
            <p>How many units of this item fit on one cart?</p>
            <input
              type="number"
              value={cartQuantityInput}
              onChange={(e) => setCartQuantityInput(e.target.value)}
              placeholder="Enter cart quantity"
              min="1"
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={handleCartQuantitySubmit} className="submit-btn">
                Save & Continue
              </button>
              <button onClick={() => {
                setCartQuantityPrompt(null);
                setCartQuantityInput('');
              }} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default CartList;